// Deterministic gradient artwork for anything that has no image yet
// (thumbnails, avatars, ambient glow). Same seed → same picture everywhere.

function hash(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

// Hue families that sit well next to the Sweet pink.
const HUES = [338, 326, 350, 300, 284, 262, 205, 188, 18, 8, 316, 232];

function hues(seed: string) {
  const h = hash(seed);
  const a = HUES[h % HUES.length];
  const b = (a + 30 + ((h >>> 5) % 90)) % 360;
  const c = HUES[(h >>> 11) % HUES.length];
  return { h, a, b, c };
}

export function thumbBackground(seed: string): string {
  const { h, a, b, c } = hues(seed);
  const p = (shift: number, min = 10, span = 80) => min + ((h >>> shift) % span);
  const angle = (h >>> 3) % 360;
  return [
    `radial-gradient(55% 75% at ${p(2)}% ${p(6)}%, hsl(${a} 72% 52% / .8), transparent 70%)`,
    `radial-gradient(45% 65% at ${p(10)}% ${p(14)}%, hsl(${b} 62% 42% / .75), transparent 72%)`,
    `radial-gradient(30% 40% at ${p(18, 25, 50)}% ${p(22, 25, 50)}%, hsl(${c} 85% 75% / .35), transparent 70%)`,
    `linear-gradient(${angle}deg, hsl(${a} 40% 14%), hsl(${b} 35% 7%))`,
  ].join(", ");
}

export function avatarBackground(seed: string): string {
  const { a, b } = hues(seed);
  return `linear-gradient(140deg, hsl(${a} 62% 58%), hsl(${b} 55% 30%))`;
}

/** Bright gradient for logo-style initials on a dark disc (studio placeholders). */
export function markBackground(seed: string): string {
  const { a, b } = hues(seed);
  return `linear-gradient(140deg, hsl(${a} 90% 72%), hsl(${b} 85% 60%))`;
}

/**
 * Three soft blobs for the blurred glow behind page headers, in the theme's own colours (--ambient-1…3 in
 * globals.css: cherry, wine and rose in Cherry Night, blush pinks in Sweet Blush). A seed moves them a little, so
 * pages differ without leaving the palette.
 */
export function ambientBackground(seed?: string): string {
  const h = seed ? hash(seed) : 0;
  const at = (shift: number, from: number, span: number) => from + ((h >>> shift) % span);
  return [
    `radial-gradient(40% 75% at ${seed ? at(2, 4, 14) : 12}% 0%, var(--ambient-1), transparent 70%)`,
    `radial-gradient(48% 85% at ${seed ? at(7, 44, 18) : 55}% 6%, var(--ambient-2), transparent 70%)`,
    `radial-gradient(36% 62% at ${seed ? at(12, 82, 14) : 92}% 0%, var(--ambient-3), transparent 70%)`,
  ].join(", ");
}
